/**
 * Token Storage Utilities
 * Enhanced storage utilities for PASETO tokens with server-provided metadata
 */

import { TOKEN_KEYS } from './token-utils';

/**
 * Token metadata from server (for encrypted PASETO tokens)
 */
export interface TokenMetadata {
  /** Token expiration timestamp (seconds since epoch) */
  exp: number;
  /** Token issued at timestamp (seconds since epoch) */
  iat: number;
  /** User ID */
  sub: string;
  /** Token type */
  type?: string;
  /** Additional server-provided claims */
  [key: string]: any;
}

/**
 * Store token with metadata. LAUNCH GATE: No tokens in browser; cookie-only BFF. No-op.
 */
export function storeTokenWithMetadata(
  _token: string,
  _metadata: TokenMetadata,
  _refreshToken?: string
): void {
  // No-op
}

/** LAUNCH GATE: No token metadata in browser. Always null. */
export function getStoredTokenMetadata(): TokenMetadata | null {
  return null;
}

/**
 * Check if stored token metadata indicates expiration
 * This is faster than decoding the token
 *
 * @param bufferMs - Buffer time before expiration (default: 5 minutes)
 * @returns True if token is expired or will expire within buffer time
 */
export function isStoredTokenExpiredByMetadata(bufferMs: number = 5 * 60 * 1000): boolean {
  const metadata = getStoredTokenMetadata();
  if (!metadata) return true;

  const now = Date.now();
  const expirationTime = metadata.exp * 1000;
  const expirationWithBuffer = expirationTime - bufferMs;

  return now >= expirationWithBuffer;
}

/**
 * Get time until stored token expires based on metadata
 *
 * @returns Milliseconds until expiration (negative if expired)
 */
export function getTimeUntilStoredTokenExpiryByMetadata(): number {
  const metadata = getStoredTokenMetadata();
  if (!metadata) return -1;

  const now = Date.now();
  const expirationTime = metadata.exp * 1000;

  return expirationTime - now;
}

/**
 * Clear all token-related storage
 */
export function clearAllTokenStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.USER);
    localStorage.removeItem(TOKEN_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem('app_token_info');

    console.log('All token storage cleared');
  } catch (error) {
    console.error('Failed to clear token storage:', error);
  }
}

/** LAUNCH GATE: No token in browser. No-op. */
export function updateTokenMetadata(_metadata: TokenMetadata): void {
  // No-op
}
